import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, MapPin, Train, ShoppingBag } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { COMPANY } from "@/lib/company";
import { NEAR_PLACE_KIND_LABEL } from "@/content/near/kinds";
import type { NearPlace } from "@/content/near/places";
import {
  BANGKOK_CORE_SERVICES,
} from "@/content/bangkok/districts";
import type { CoreServicePillar } from "@/content/service-pillars/core.gen";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";

function shortTitle(t: string): string {
  return t.replace(/\s*\|\s*Thai Notary.*/i, "").trim();
}

function buildFaqs(p: NearPlace) {
  return [
    {
      q: `Notary ใกล้ ${p.nameTh} เดินทางถึงภายในกี่ชั่วโมง?`,
      a: `ทีมทนายและทีมแปลเดินทาง on-site ใกล้ ${p.nameTh} (${p.nameEn}) ในเขต${p.districtNameTh} — แจ้งก่อนเที่ยง ทีมถึงในบ่ายวันเดียวกัน รวมเสาร์–อาทิตย์`,
    },
    {
      q: `รับเคสที่คอนโด/โรงแรม/คาเฟ่ใกล้ ${p.nameTh} ได้ไหม?`,
      a: `ได้ครับ — ทีมมีอุปกรณ์รับรองครบชุด ใช้พื้นที่ล็อบบี้คอนโด ห้องประชุมโรงแรม หรือคาเฟ่ใกล้ ${p.nearby.slice(0, 2).join(" / ")} เซ็นเอกสารต่อหน้าทนายได้ทันที`,
    },
    {
      q: `ใกล้ ${p.nameTh} มีบริการอะไรบ้าง?`,
      a: `Notary Public, แปลรับรอง 30+ ภาษา, MFA Consular Legalization, สถานทูตทุกประเทศ, ตรวจประวัติอาชญากรรม, จดทะเบียนสมรสไทย/ต่างชาติ และวีซ่าไทย/ต่างประเทศ — เริ่มต้น 1,500 บาท`,
    },
    {
      q: `ค่าเดินทาง on-site คิดยังไง?`,
      a: `คิดตามระยะจริงจากออฟฟิศกรุงเทพ — สำหรับจุดนัดใกล้ ${p.nameTh} ส่วนใหญ่อยู่ที่ 200–500 บาท แจ้งราคาเต็มก่อนยืนยันงานเสมอ ไม่มีค่าใช้จ่ายแอบแฝง`,
    },
    {
      q: `${p.expatNote.split("—")[0].trim()} — เคยใช้บริการของ Thai Notary ที่นี่ไหม?`,
      a: `${p.expatNote} ทีมมีประสบการณ์รับเคสในจุดนัดใกล้ ${p.nameTh} ต่อเนื่องตั้งแต่ปี 2566 รวมเคสด่วน last-minute ก่อนบินจากสุวรรณภูมิและดอนเมือง`,
    },
  ];
}

export const Route = createFileRoute("/near/$place")({
  loader: async ({
    params,
  }): Promise<{ place: NearPlace; services: CoreServicePillar[] }> => {
    const { getNearPlace } = await import("@/content/near/places");
    const p = getNearPlace(params.place);
    if (!p) throw notFound();
    const { getCoreServicePillar } = await import(
      "@/content/service-pillars/core.gen"
    );
    const services = BANGKOK_CORE_SERVICES.map((s) =>
      getCoreServicePillar(s),
    ).filter(Boolean) as CoreServicePillar[];
    return { place: p, services };
  },
  head: ({ params, loaderData }) => {
    const p = loaderData?.place;
    if (!p) {
      return pageHeadForProvince({
        title: "ไม่พบจุดนัด",
        description: "ไม่พบจุดนัดนี้",
        path: `/near/${params.place}`,
      });
    }
    const site = getActiveSiteUrl();
    const kindLabel = NEAR_PLACE_KIND_LABEL[p.kind];
    const rawTitle = `Notary ใกล้ ${p.nameTh} — On-site วันเดียวกัน`;
    const title = rawTitle.length > 60 ? rawTitle.slice(0, 59) + "…" : rawTitle;
    const rawDesc = `Notary, แปลรับรอง, MFA & วีซ่า on-site ใกล้ ${p.nameTh} (${p.nameEn}) เขต${p.districtNameTh} — ทีมถึงคอนโด/โรงแรมใกล้ ${p.nearby.slice(0, 2).join(", ")} ภายในวันเดียวกัน`;
    const desc = rawDesc.length > 158 ? rawDesc.slice(0, 157) + "…" : rawDesc.length < 120 ? `${rawDesc}. ทีม Notarial Services Attorney + เดิน MFA แจ้งวัฒนะ + สถานทูตให้ครบจบ.`.slice(0, 158) : rawDesc;
    const base = pageHeadForProvince({ title, description: desc, path: `/near/${p.slug}` });
    const faqs = buildFaqs(p);
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "Near me", url: `${site}/near` },
              { name: p.nameTh, url: `${site}/near/${p.slug}` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Place",
            "@id": `${site}/near/${p.slug}#place`,
            name: `${p.nameTh} (${p.nameEn})`,
            description: p.intro,
            ...(p.kind === "mall"
              ? { additionalType: "https://schema.org/ShoppingCenter" }
              : { additionalType: "https://schema.org/TrainStation" }),
            address: {
              "@type": "PostalAddress",
              addressLocality: p.districtNameTh,
              addressRegion: "Bangkok",
              addressCountry: "TH",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: p.geo.lat,
              longitude: p.geo.lng,
            },
            containedInPlace: { "@type": "City", name: "Bangkok" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": `${site}/near/${p.slug}#localbusiness`,
            name: `Thai Notary — On-site near ${p.nameEn}`,
            image: `${site}/og-image.jpg`,
            url: `${site}/near/${p.slug}`,
            telephone: COMPANY.phone,
            priceRange: "฿฿",
            address: {
              "@type": "PostalAddress",
              addressLocality: p.districtNameTh,
              addressRegion: "Bangkok",
              addressCountry: "TH",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: p.geo.lat,
              longitude: p.geo.lng,
            },
            serviceArea: {
              "@type": "GeoCircle",
              geoMidpoint: {
                "@type": "GeoCoordinates",
                latitude: p.geo.lat,
                longitude: p.geo.lng,
              },
              geoRadius: 1500,
            },
            areaServed: {
              "@type": "Place",
              name: `${kindLabel} ${p.nameTh}`,
            },
            openingHours: "Mo-Su 08:00-20:00",
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/near/${params.place}`)) },
      ],
    };
  },
  component: NearPlacePage,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">ไม่พบจุดนัดนี้</h1>
        <Link to="/near" className="mt-4 inline-flex items-center gap-1 text-gold">
          <ArrowLeft className="h-4 w-4" /> กลับรายการ
        </Link>
      </div>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="p-10 text-center">
      <h1 className="text-xl font-bold">เกิดข้อผิดพลาด</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
});

function NearPlacePage() {
  const data = Route.useLoaderData() as {
    place: NearPlace;
    services: CoreServicePillar[];
  };
  const p = data.place;
  const kindLabel = NEAR_PLACE_KIND_LABEL[p.kind];
  const Icon = p.kind === "mall" ? ShoppingBag : Train;
  const services = data.services;
  const faqs = buildFaqs(p);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <nav className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-5xl px-4 py-3 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <span className="mx-2">/</span>
            <Link to="/near" className="hover:text-primary">Near me</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">{p.nameTh}</span>
          </div>
        </nav>

        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <div className="text-xs font-bold uppercase tracking-widest text-gold">
              <Icon className="mr-1 inline h-3.5 w-3.5" />
              {kindLabel}{p.line ? ` · ${p.line}` : ""}
            </div>
            <h1 className="mt-2 text-3xl font-extrabold md:text-4xl">
              Notary, แปลรับรอง, MFA & วีซ่า — ใกล้ {p.nameTh}
            </h1>
            <p id="ai-answer-tldr" className="mt-4 rounded-2xl border border-gold/30 bg-gold/5 p-5 text-base leading-relaxed">
              <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-gold">สรุปสั้น</span>
              {p.intro} ทีม Thai Notary รับเคส on-site ที่คอนโด/สำนักงาน/โรงแรม/คาเฟ่ใกล้ {p.nearby.slice(0, 2).join(", ")} ในเขต{p.districtNameTh} — ทนายและทีมแปลเดินทางถึงภายในวันเดียวกัน
            </p>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto grid max-w-5xl gap-3 px-4 py-8 md:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">
                <MapPin className="mr-1 inline h-3.5 w-3.5 text-gold" /> Landmark ใกล้
              </div>
              <div className="mt-1 text-sm font-bold">{p.nearby.join(", ")}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">
                เขตปกครอง
              </div>
              <div className="mt-1 text-sm font-bold">
                <Link to="/bangkok/$district" params={{ district: p.districtSlug }} className="text-primary hover:text-gold">
                  {p.districtNameTh}
                </Link>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">
                ผู้ใช้บริการบ่อย
              </div>
              <div className="mt-1 text-sm font-bold">{p.expatNote.split("—")[0].trim()}</div>
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">บริการ on-site ใกล้ {p.nameTh}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              เลือกบริการเพื่อดูขั้นตอน เอกสาร และราคา — ทีมรับเคสรวมเสาร์–อาทิตย์
            </p>
            <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {services.map((s) => (
                <Link
                  key={s.slug}
                  to="/services/$service"
                  params={{ service: s.slug }}
                  className="group rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-gold"
                >
                  <h3 className="text-base font-bold text-primary group-hover:text-gold">
                    {shortTitle(s.h1).slice(0, 60)}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{s.metaDesc}</p>
                  {s.pricing[0]?.price && (
                    <div className="mt-3 text-sm font-bold text-gold">เริ่ม {s.pricing[0].price}</div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">ทำไมเลือกเรา สำหรับเคสใกล้ {p.nameTh}</h2>
            <ul className="mt-4 grid gap-3 md:grid-cols-2">
              {[
                `ทีมทนาย Notarial Services Attorney ขึ้นทะเบียนสภาทนายความฯ — รับรองได้ถูกต้องตามที่ MFA และสถานทูตทุกประเทศยอมรับ`,
                `รับเคส on-site ใกล้ ${p.nameTh} ในระยะเดินเท้า 5–10 นาทีจากจุดนัด (${p.nearby.slice(0, 2).join(", ")})`,
                `ทีมแปลครอบคลุม 30+ ภาษา — ส่งงานทันทีพร้อมแนบ Translation Certificate`,
                `เดินเอกสาร MFA Consular (แจ้งวัฒนะ) และสถานทูตทุกประเทศในกรุงเทพให้ครบจบ`,
                `รองรับเคสด่วนวันเดียวกัน — ${p.expatNote}`,
                `${COMPANY.trustLine} — ใบเสร็จเต็มรูปแบบ`,
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-2 rounded-xl border border-border bg-card p-4 text-sm">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-3xl px-4 py-12">
            <h2 className="text-2xl font-bold">คำถามที่พบบ่อย</h2>
            <div className="mt-5 space-y-3">
              {faqs.map((f, i) => (
                <details key={i} className="group rounded-xl border border-border bg-card p-4">
                  <summary className="cursor-pointer font-semibold text-primary">{f.q}</summary>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 py-12 text-center">
            <h2 className="text-2xl font-extrabold">พร้อมนัดทีมที่ {p.nameTh} แล้วใช่ไหม?</h2>
            <p className="mt-3 text-sm opacity-90">
              ทักไลน์ส่งภาพเอกสาร ทีมประเมินภายใน 30 นาที — รับเคส on-site ทุกวันรวมเสาร์–อาทิตย์
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <a href={`tel:${COMPANY.phoneIntl}`} className="rounded-md bg-gold px-5 py-2.5 text-sm font-bold text-foreground">
                โทร {COMPANY.phone}
              </a>
              <a href={`https://line.me/R/ti/p/${COMPANY.lineId}`} target="_blank" rel="noopener" className="rounded-md border border-primary-foreground/30 bg-primary-foreground/10 px-5 py-2.5 text-sm font-bold">
                ทักไลน์ {COMPANY.lineId}
              </a>
            </div>
          </div>
        </section>
        <div className="mx-auto max-w-4xl px-4 pb-12">
          <FaqSourceNote topic="notary" />
        </div>

      </main>
      <SiteFooter />
    </div>
  );
}
